// Offline sale queue.
// Completed POS sales are persisted locally when the network is down and
// retried against the API once connectivity returns.

import api from "./api";

const QUEUE_KEY = "kankali-pos-offline-queue";

const readQueue = () => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Storage full — drop the oldest entry to stay bounded
    const trimmed = queue.slice(1);
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
    } catch {
      // give up silently
    }
  }
};

export const getQueuedSales = () => readQueue();

export const enqueueSale = (payload, { billToo = true } = {}) => {
  const queue = readQueue();
  const entry = {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    billToo,
    payload,
  };
  writeQueue([...queue, entry]);
  return entry;
};

export const removeQueuedSale = (id) => {
  writeQueue(readQueue().filter((entry) => entry.id !== id));
};

export const clearQueue = () => writeQueue([]);

export const queuedCount = () => readQueue().length;

/**
 * Push every queued sale to the API in order. Returns the results so callers
 * can show a summary.
 */
export const syncQueuedSales = async ({ onProgress } = {}) => {
  const queue = readQueue();
  const synced = [];
  const failed = [];

  for (const entry of queue) {
    try {
      const response = await api.post("/admin/offline-sales", entry.payload);
      const sale = response.data?.data ?? response.data ?? {};

      if (entry.billToo && sale?.id) {
        try {
          await api.post(`/admin/offline-sales/${sale.id}/generate-bill`, {
            billing_type: entry.payload.billing_type || "vat",
            vat_rate: entry.payload.vat_rate ?? 13,
          });
        } catch {
          // Bill generation is best-effort; the recorded sale is already saved.
        }
      }

      removeQueuedSale(entry.id);
      synced.push({ id: entry.id, saleNumber: sale.sale_number });
      if (onProgress) onProgress(synced.length, failed.length, queue.length);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.line_items?.[0] ||
        error?.message ||
        "Sync failed";
      failed.push({ id: entry.id, error: msg });
      if (onProgress) onProgress(synced.length, failed.length, queue.length);
    }
  }

  return { synced, failed };
};

export const isSaleAlreadyQueued = (payload) => {
  const signature = JSON.stringify({
    items: payload.line_items,
    total: payload.subtotal,
  });
  return readQueue().some((entry) => {
    try {
      const other = JSON.stringify({
        items: entry.payload.line_items,
        total: entry.payload.subtotal,
      });
      return other === signature;
    } catch {
      return false;
    }
  });
};
